import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLogout, useCurrentUser } from "utils/hooks";
import {
  updatePracticeSubmissionAction,
  createSubmissionAction,
} from "store/actions/submissions";

type AssignSubmissionToPatientProps = {
  submissionId: string | undefined;
};
type Props = {
  patientId: string | null;
};

const useAssignSubmissionToPatient = ({
  submissionId,
}: AssignSubmissionToPatientProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common"]);
  const { logOut } = useLogout();
  const { user } = useCurrentUser();
  const [loading, setLoading] = useState(false);

  const assignSubmissionToPatient = async ({ patientId }: Props) => {
    if (!patientId || !submissionId) return null;

    setLoading(true);

    const serverCall = async () => {
      await fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          query: `
                mutation {
                    assignSubmissionToPatient(
                        patientId: "${patientId}",
                        submissionId: "${submissionId}",
                    ) {
                        uuid
                        createdAt
                        slug
                        userId
                        authorId
                        assignmentId
                        assignmentLabel
                        privacy
                        color
                        isDraft
                        groupId
                        groupName
                        groupPrivacy
                        isWideData
                        category
                        hideFromPatient
                        userName
                        userEmail
                        therapistId
                        connectedTherapistId
                        icon {
                        name
                        src
                        }
                        responseFields {
                        uuid
                        submissionId
                        sectionId
                        sectionLabel
                        sectionOrder
                        fieldId
                        fieldLabel
                        fieldType
                        value
                        includeInScoreCalculation
                        }
                        submissionScore {
                        scoringInstructions
                        scoringType
                        totalScore
                        color
                        fieldsetScores {
                            name
                            score
                            scoringType
                        }
                        scoreDisplays {
                            id
                            name
                            min
                            max
                            color
                            order
                        }
                        scoreSuffix
                        scoreAsPercentage
                        }
                    }
                    }
                  `,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: user?.activeOrganizationId || "NA",
          permissionKey: "add_patients",
        },
      })
        .then(async (res) => {
          if (res.status !== 200 && res.status !== 201) {
            const error = await res.json();
            if (
              error.errors &&
              error.errors[0] &&
              error.errors[0].message.includes("BRKN_6001")
            ) {
              toast.warn(t("errors.login_again_error"));
              logOut();
            } else {
              toast.error(t("errors.something_went_wrong"));
              throw new Error(t("errors.failed_error"));
            }
          }
          return res.json();
        })
        .then((resData) => {
          if (resData.errors) {
            toast.error(
              `${t("errors.something_went_wrong")} ${resData.errors[0].message}`
            );
          } else {
            dispatch(
              createSubmissionAction(resData.data.assignSubmissionToPatient)
            );
            dispatch(
              updatePracticeSubmissionAction(
                resData.data.assignSubmissionToPatient
              )
            );
            toast(
              t("inbox.assign_submission_success", {
                patientWord: resData.data.assignSubmissionToPatient.userName,
              })
            );
            return true;
          }
        })
        .catch(() => {
          return false;
        })
        .finally(() => {
          setLoading(false);
        });
    };

    return await serverCall();
  };

  return {
    assignSubmissionToPatient,
    loading,
  };
};

export default useAssignSubmissionToPatient;
